// ==========================================================================
// components/TaskSheet.tsx — add or change a task (028, 10 Oct 2026)
//   James: "did we make it so we can assign tasks and maybe turn a shopping
//   list into a task to go to the shops and link it a bit for easy nav".
//   Who it's for, when, a note; a shopping trip links to the Kitchen list.
// ==========================================================================
import { useState } from "react";
import { Link } from "react-router-dom";
import { addDays } from "date-fns";
import { ShoppingBasket, Trash2 } from "lucide-react";
import Sheet, { Lbl, inputCls } from "./Sheet";
import { ErrorText } from "./ui";
import { usePeople } from "@/lib/household";
import { tokenColour, ymd } from "@/lib/calendar";
import { useAddTask, useUpdateTask, type Task, type TaskKind } from "@/lib/tasks";

export type TaskSeed = { title: string; kind: TaskKind; assignee_id?: string | null; due_on?: string | null; note?: string | null };

export default function TaskSheet({ task, seed, onClose, listCount }: { task?: Task; seed?: TaskSeed; onClose: () => void; listCount?: number }) {
  const people = usePeople(); const add = useAddTask(); const upd = useUpdateTask();
  const adults = (people.data ?? []).filter((p) => p.role === "adult");
  const start = task ?? seed ?? { title: "", kind: "todo" as TaskKind };
  const [title, setTitle] = useState(start.title);
  const [who, setWho] = useState<string | null>(start.assignee_id ?? null);
  const [due, setDue] = useState(start.due_on ?? "");
  const [note, setNote] = useState(start.note ?? "");
  const [err, setErr] = useState<string | null>(null);
  const today = ymd(new Date()); const tomorrow = ymd(addDays(new Date(), 1));
  const shop = start.kind === "shopping";

  const save = () => {
    if (!title.trim()) { setErr("What needs doing?"); return; }
    const row = { title: title.trim(), assignee_id: who, due_on: due || null, note: note.trim() || null };
    if (task) upd.mutate({ id: task.id, patch: row }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
    else add.mutate({ ...row, kind: start.kind }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  const chip = (on: boolean, colour?: string) => ({ className: `rounded-full px-3.5 py-2 text-sm transition ${on ? "font-semibold text-white" : "bg-surface-raised text-ink"}`, style: on ? { background: tokenColour(colour ?? "panel") } : undefined });

  return (
    <Sheet title={task ? (shop ? "Shopping trip" : "Task") : shop ? "Who's doing the shop?" : "New task"} onClose={onClose}>
      <div className="space-y-5">
        {shop && (
          <Link to="/kitchen/list" onClick={onClose} className="flex items-center gap-3 rounded-xl bg-primary/10 p-4 text-primary">
            <ShoppingBasket className="h-6 w-6 shrink-0" />
            <span className="text-sm"><b className="block">Open the shopping list</b>{listCount !== undefined ? `${listCount} ${listCount === 1 ? "thing" : "things"} to get` : "Linked to this trip"}</span>
          </Link>
        )}
        <Lbl label="What"><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!shop} /></Lbl>
        <div>
          <span className="text-sm font-medium text-ink-muted">Who's doing it</span>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setWho(null)} {...chip(who === null)}>Anyone</button>
            {adults.map((p) => <button key={p.id} type="button" onClick={() => setWho(p.id)} {...chip(who === p.id, p.colour)}>{p.display_name}</button>)}
          </div>
        </div>
        <div>
          <span className="text-sm font-medium text-ink-muted">When</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setDue(today)} {...chip(due === today, "primary")}>Today</button>
            <button type="button" onClick={() => setDue(tomorrow)} {...chip(due === tomorrow, "primary")}>Tomorrow</button>
            <button type="button" onClick={() => setDue("")} {...chip(!due, "primary")}>No date</button>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="rounded-full bg-surface-raised px-3 py-2 text-sm ring-1 ring-line" aria-label="Pick a date" />
          </div>
        </div>
        <Lbl label="Note"><textarea className={`${inputCls} min-h-[70px]`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={shop ? "Which shop, a budget, 'grab petrol too'…" : "Anything they need to know"} /></Lbl>
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={add.isPending || upd.isPending} className="flex-1 rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">{task ? "Save" : shop ? "Make it a task" : "Add task"}</button>
          {task && <button type="button" onClick={() => upd.mutate({ id: task.id, patch: { remove: true } }, { onSuccess: onClose })} className="inline-flex items-center gap-1.5 rounded-full bg-surface-raised px-4 py-3 text-sm font-semibold text-bad"><Trash2 className="h-4 w-4" />Remove</button>}
        </div>
      </div>
    </Sheet>
  );
}
